import { nanoid } from 'nanoid'
import _ from 'lodash'

const squadronColors = [
  'Red',
  'Blue',
  'Green',
  'Purple',
  'Orange',
  'Yellow',
  'Rust',
  'Pink',
  'Gray',
  'Sunlight'
]

const squadronBackgroundColors = [
  '#d23b42',
  '#34558b',
  '#575c46',
  '#71617c',
  '#ff823f',
  '#ffaf15',
  '#a2543d',
  '#eaac9d',
  '#a19d9c',
  '#eed9a2'
]

const squadronHelper = {
  getColor: (number) => {
    if (number > squadronColors.length) {
      return number
    }
    return squadronColors[number]
  },
  getBackgroundColor: (number) => {
    if (number > squadronBackgroundColors.length) {
      return number
    }
    return squadronBackgroundColors[number]
  }
}

const generateAvailablePoolSizes = (participants) => {
  const pools = []
  let preferred = ''

  const registerPool = (pool) => {
    if (pool.poolSize < 7 || (pool.poolSize === 7 && pool.overloaded === 0)) {
      preferred = pool.id
    }
    pools.push(pool)
  }

  for (let size = 2; size <= Math.min(15, participants); size += 1) {
    if (participants % size === 0) {
      registerPool({
        text: size,
        id: size,
        numPools: participants / size,
        poolSize: size
      })
    }

    for (let p = 1; p < participants / size; p += 1) {
      if ((participants - (size * p)) % (size + 1) === 0) {
        registerPool({
          text: `${size} or ${size + 1}`,
          id: `${size}_${size + 1}`,
          numPools: p + (participants - size * p) / (size + 1),
          poolSize: size,
          overloaded: (participants - size * p) / (size + 1)
        })
        break
      }
    }
  }

  return pools.map(p => {
    if (p.id === preferred) {
      p.preferred = true
    }
    return p
  })
}

const populatePools = (config, participants) => {
  const pools = []
  if (participants?.length > 0) {
    const randomSeeds = participants?.map(p => {
      if (!p.seed) {
        return Object.assign({}, p, { seed: nanoid() })
      }
      return p
    })

    const sorted = randomSeeds?.sort((a, b) => {
      if (a.rating === b.rating) {
        if (a.club === b.club) {
          return a.seed < b.seed ? -1 : 1
        } else {
          return a.club < b.club ? -1 : 1
        }
      } else {
        return a.rating < b.rating ? -1 : 1
      }
    })

    _.times(config?.numPools, (i) => { pools.push({ index: i, participants: [] }) })

    let index = 0
    let iterator = 1
    for (const particpant of sorted) {
      particpant.randomSeed = particpant.seed
      particpant.seed = pools[index].participants.length
      pools[index].participants.push(particpant)

      index += iterator
      if (index >= pools.length) {
        iterator = -1
        index = pools.length - 1
      }
      if (index < 0) {
        iterator = 1
        index = 0
      }
    }
  }

  return pools
}

const matchData = {
  2: [[1, 2]],
  3: [[2, 3], [1, 3], [1, 2]],
  4: [[1, 4], [2, 3], [1, 3], [2, 4], [3, 4], [1, 2]],
  5: [[1, 2], [3, 4], [5, 1], [2, 3], [5, 4], [1, 3], [2, 5], [4, 1], [3, 5], [4, 2]],
  6: [[1, 2], [4, 3], [6, 5], [3, 1], [2, 6], [5, 4], [1, 6], [3, 5], [4, 2], [5, 1], [6, 4], [2, 3], [1, 4], [5, 2], [3, 6]],
  7: [[1, 4], [2, 5], [3, 6], [7, 1], [5, 4], [2, 3], [6, 7], [5, 1], [4, 3], [6, 2], [5, 7], [3, 1], [4, 6], [7, 2], [3, 5], [1, 6], [2, 4], [7, 3], [6, 5], [1, 2], [4, 7]],
  8: [[2, 3], [1, 5], [7, 4], [6, 8], [1, 2], [3, 4], [5, 6], [8, 7], [4, 1], [5, 2], [8, 3], [6, 7], [4, 2], [8, 1], [7, 5], [3, 6], [2, 8], [5, 4], [6, 1], [3, 7], [4, 8], [2, 6], [3, 5], [1, 7], [4, 6], [8, 5], [7, 2], [1, 3]],
  9: [[1, 9], [2, 8], [3, 7], [4, 6], [1, 5], [2, 9], [8, 3], [7, 4], [6, 5], [1, 2], [9, 3], [8, 4], [7, 2], [6, 1], [3, 2], [9, 4], [5, 8], [7, 6], [3, 1], [2, 4], [5, 9], [8, 6], [7, 1], [4, 3], [5, 2], [6, 9], [8, 7], [4, 1], [5, 3], [6, 2], [9, 7], [1, 8], [4, 5], [3, 6], [5, 7], [9, 8]],
  10: [[1, 4], [6, 9], [2, 5], [7, 10], [3, 1], [8, 6], [4, 5], [9, 10], [2, 3], [7, 8], [5, 1], [10, 6], [4, 2], [9, 7], [5, 3], [10, 8], [1, 2], [6, 7], [3, 4], [8, 9], [5, 10], [1, 6], [2, 7], [3, 8], [4, 9], [6, 5], [10, 2], [8, 1], [7, 4], [9, 3], [2, 6], [5, 8], [4, 10], [1, 9], [3, 7], [8, 2], [6, 4], [9, 5], [10, 3], [7, 1], [4, 8], [2, 9], [3, 6], [5, 7], [1, 10]],
  11: [[1, 2], [7, 8], [4, 5], [10, 11], [2, 3], [8, 9], [5, 6], [3, 1], [9, 7], [6, 4], [2, 5], [8, 11], [1, 4], [7, 10], [5, 3], [11, 9], [1, 6], [4, 2], [10, 8], [3, 6], [5, 1], [11, 7], [3, 4], [9, 10], [6, 2], [1, 7], [3, 9], [10, 4], [8, 2], [5, 11], [1, 8], [9, 2], [3, 10], [4, 11], [6, 7], [9, 1], [2, 10], [11, 3], [7, 5], [6, 8], [10, 1], [11, 2], [4, 7], [8, 5], [6, 9], [11, 1], [7, 3], [4, 8], [9, 5], [6, 10], [2, 7], [8, 3], [4, 9], [10, 5], [6, 11]],
  12: [[1, 2], [3, 4], [5, 6], [7, 8], [9, 10], [11, 12], [3, 1], [2, 4], [7, 5], [6, 8], [11, 9], [10, 12], [4, 1], [2, 3], [8, 5], [6, 7], [12, 9], [10, 11], [1, 5], [4, 8], [6, 2], [7, 3], [9, 1], [12, 5], [4, 10], [8, 11], [2, 7], [3, 6], [5, 9], [1, 12], [8, 10], [11, 4], [5, 2], [9, 7], [12, 3], [1, 6], [10, 2], [5, 11], [8, 9], [4, 7], [3, 10], [12, 6], [11, 1], [2, 8], [9, 4], [7, 10], [5, 3], [6, 11], [2, 12], [1, 8], [4, 5], [9, 3], [7, 11], [10, 6], [8, 12], [9, 2], [7, 1], [6, 4], [3, 11], [10, 5], [12, 7], [6, 9], [8, 3], [1, 10], [4, 12], [11, 2]],
  13: [[1, 2], [3, 4], [5, 6], [7, 8], [9, 10], [11, 12], [13, 1], [2, 3], [4, 5], [6, 7], [8, 9], [10, 11], [12, 1], [2, 13], [3, 5], [4, 6], [7, 9], [8, 10], [1, 11], [12, 2], [13, 3], [5, 7], [9, 4], [6, 8], [10, 1], [11, 2], [3, 12], [5, 13], [7, 4], [9, 6], [1, 8], [2, 10], [11, 3], [12, 5], [4, 13], [1, 7], [6, 2], [3, 9], [8, 11], [10, 5], [4, 12], [13, 7], [6, 1], [2, 9], [8, 3], [5, 11], [10, 4], [7, 12], [13, 6], [9, 1], [2, 8], [11, 4], [3, 10], [12, 6], [9, 13], [1, 5], [7, 2], [4, 8], [6, 11], [10, 12], [1, 3], [8, 13], [5, 9], [11, 7], [6, 10], [12, 8], [2, 4], [13, 11], [3, 7], [8, 5], [9, 12], [10, 13], [4, 1], [3, 6], [5, 2], [11, 9], [7, 10], [12, 13]],
  14: [[1, 2], [3, 4], [5, 6], [7, 8], [9, 10], [11, 12], [13, 14], [3, 1], [2, 4], [7, 5], [6, 8], [11, 9], [10, 12], [1, 13], [14, 3], [2, 5], [4, 7], [6, 9], [8, 11], [10, 1], [12, 13], [3, 2], [5, 14], [4, 6], [9, 7], [8, 1], [11, 10], [12, 2], [13, 3], [4, 5], [14, 6], [1, 7], [8, 9], [2, 10], [11, 3], [12, 4], [5, 13], [6, 1], [7, 14], [2, 8], [9, 3], [10, 4], [12, 14], [5, 11], [13, 6], [7, 2], [1, 12], [14, 8], [3, 10], [4, 9], [1, 5], [6, 11], [12, 7], [2, 13], [8, 3], [10, 14], [4, 1], [9, 5], [6, 7], [11, 2], [3, 12], [13, 8], [14, 1], [5, 10], [11, 4], [2, 9], [3, 6], [7, 13], [8, 12], [1, 11], [4, 14], [5, 3], [10, 6], [9, 13], [14, 2], [7, 11], [8, 4], [12, 5], [1, 9], [13, 10], [6, 2], [3, 7], [9, 12], [14, 11], [13, 4], [5, 8], [10, 7], [12, 6], [11, 13], [9, 14], [8, 10]],
  15: [[1, 2], [3, 4], [5, 6], [7, 8], [9, 10], [11, 12], [13, 14], [15, 1], [2, 3], [4, 5], [6, 7], [8, 9], [10, 11], [12, 13], [14, 1], [2, 15], [3, 5], [4, 6], [7, 9], [8, 10], [11, 13], [1, 12], [14, 2], [15, 3], [5, 7], [9, 4], [6, 8], [10, 13], [1, 11], [12, 2], [3, 14], [5, 15], [4, 7], [6, 9], [13, 8], [10, 1], [2, 11], [12, 3], [14, 5], [15, 4], [7, 13], [1, 6], [9, 2], [8, 11], [3, 10], [5, 12], [4, 14], [7, 15], [13, 1], [2, 6], [11, 9], [8, 3], [10, 5], [12, 4], [14, 7], [13, 15], [9, 1], [6, 11], [2, 8], [3, 7], [4, 10], [13, 5], [14, 12], [15, 9], [1, 8], [6, 3], [7, 11], [4, 2], [10, 12], [9, 5], [15, 14], [13, 3], [1, 7], [8, 4], [10, 6], [11, 5], [12, 9], [2, 13], [3, 1], [8, 14], [6, 15], [7, 10], [11, 4], [5, 2], [9, 13], [12, 8], [14, 6], [1, 4], [11, 3], [15, 10], [2, 7], [5, 8], [13, 6], [9, 14], [12, 15], [5, 1], [10, 2], [11, 14], [7, 12], [4, 13], [8, 15], [3, 9], [6, 12], [14, 10], [15, 11]],
  16: [[1, 2], [3, 4], [5, 6], [7, 8], [9, 10], [11, 12], [13, 14], [15, 16], [3, 1], [2, 4], [7, 5], [6, 8], [11, 9], [10, 12], [15, 13], [14, 16], [4, 1], [2, 3], [8, 5], [6, 7], [12, 9], [10, 11], [16, 13], [14, 15], [1, 5], [4, 8], [6, 2], [7, 3], [9, 13], [12, 16], [14, 10], [15, 11], [8, 1], [5, 4], [2, 7], [3, 6], [16, 9], [13, 12], [10, 15], [11, 14], [1, 7], [8, 2], [5, 3], [4, 6], [9, 15], [16, 10], [12, 14], [13, 11], [1, 6], [2, 5], [3, 8], [4, 7], [9, 14], [10, 13], [11, 16], [12, 15], [9, 1], [6, 14], [10, 2], [5, 13], [11, 3], [8, 16], [12, 4], [7, 15], [14, 1], [6, 9], [13, 2], [5, 10], [16, 3], [8, 11], [15, 4], [7, 12], [1, 13], [2, 14], [9, 5], [10, 6], [3, 15], [4, 16], [12, 8], [11, 7], [1, 10], [2, 9], [14, 5], [3, 12], [13, 6], [4, 11], [16, 7], [15, 8], [12, 1], [10, 3], [11, 2], [9, 4], [5, 16], [7, 14], [6, 15], [8, 13], [1, 11], [2, 12], [3, 9], [4, 10], [15, 5], [16, 6], [13, 7], [14, 8], [15, 1], [5, 11], [16, 2], [6, 12], [13, 3], [7, 9], [14, 4], [8, 10], [1, 16], [2, 15], [5, 12], [6, 11], [3, 14], [4, 13], [7, 10], [8, 9]],
  17: [[1, 2], [3, 4], [5, 6], [7, 8], [9, 10], [11, 12], [13, 14], [15, 16], [17, 1], [2, 3], [4, 5], [6, 7], [8, 9], [10, 11], [12, 13], [14, 15], [16, 1], [2, 17], [3, 5], [4, 6], [7, 9], [8, 10], [11, 13], [12, 14], [1, 15], [16, 2], [17, 3], [5, 7], [9, 4], [6, 8], [10, 13], [14, 11], [1, 12], [15, 2], [3, 16], [5, 17], [4, 7], [6, 9], [13, 8], [10, 14], [11, 1], [2, 12], [15, 3], [16, 5], [17, 4], [7, 13], [10, 6], [9, 14], [8, 1], [2, 11], [12, 3], [5, 15], [4, 16], [7, 17], [13, 6], [1, 10], [14, 8], [9, 2], [3, 11], [12, 5], [15, 4], [16, 7], [6, 17], [13, 1], [2, 10], [8, 3], [14, 5], [11, 9], [4, 12], [7, 15], [6, 16], [17, 13], [1, 3], [8, 2], [5, 10], [14, 4], [16, 17], [9, 12], [11, 7], [17, 8], [15, 6], [13, 16], [1, 5], [3, 10], [2, 4], [7, 14], [12, 6], [15, 9], [16, 11], [5, 13], [10, 17], [4, 8], [7, 1], [3, 14], [6, 2], [12, 15], [9, 16], [5, 11], [13, 4], [10, 7], [14, 17], [1, 6], [8, 12], [3, 9], [2, 5], [11, 15], [16, 10], [4, 1], [13, 3], [6, 14], [12, 7], [17, 9], [5, 8], [10, 15], [13, 2], [4, 11], [3, 6], [14, 16], [9, 1], [17, 12], [2, 7], [6, 11], [8, 15], [10, 4], [9, 5], [16, 12], [1, 14], [15, 13], [11, 17], [7, 3], [8, 16], [14, 2], [12, 10], [15, 17], [13, 9], [11, 8]],
  18: [[1, 2], [3, 4], [5, 6], [7, 8], [9, 10], [11, 12], [13, 14], [15, 16], [17, 18], [3, 1], [2, 4], [7, 5], [6, 8], [11, 9], [10, 12], [15, 13], [14, 16], [1, 17], [18, 3], [2, 5], [4, 7], [6, 9], [8, 11], [10, 13], [12, 15], [14, 1], [16, 17], [3, 2], [5, 18], [4, 6], [9, 7], [8, 10], [13, 11], [12, 1], [15, 14], [16, 2], [17, 3], [4, 5], [18, 6], [7, 10], [8, 9], [1, 11], [12, 13], [2, 14], [15, 3], [16, 4], [5, 17], [6, 7], [10, 18], [1, 8], [9, 12], [11, 2], [13, 3], [14, 4], [5, 15], [6, 16], [17, 7], [10, 1], [18, 8], [2, 9], [3, 12], [4, 11], [13, 5], [14, 6], [7, 15], [1, 16], [17, 10], [8, 2], [9, 18], [11, 3], [12, 4], [5, 14], [6, 13], [7, 1], [10, 15], [16, 8], [2, 17], [3, 9], [18, 11], [12, 5], [4, 13], [14, 7], [1, 6], [8, 15], [16, 10], [9, 17], [2, 18], [3, 5], [11, 7], [14, 12], [4, 1], [13, 8], [15, 6], [9, 16], [10, 2], [17, 11], [7, 18], [14, 3], [5, 1], [8, 12], [15, 4], [13, 9], [6, 2], [11, 16], [3, 10], [17, 14], [1, 18], [12, 7], [5, 8], [4, 9], [2, 15], [16, 13], [6, 11], [10, 14], [7, 3], [12, 17], [9, 1], [18, 4], [16, 5], [2, 13], [8, 14], [11, 15], [10, 6], [3, 16], [4, 17], [18, 12], [7, 2], [5, 9], [1, 13], [11, 14], [8, 3], [15, 17], [10, 4], [6, 12], [18, 16], [13, 7], [11, 5], [9, 14], [1, 15], [12, 2], [17, 8], [3, 6], [5, 10], [16, 7], [13, 18], [4, 8], [17, 6], [15, 9], [14, 18], [11, 10], [12, 16], [13, 17], [18, 15]],
  19: [[1, 2], [3, 4], [5, 6], [7, 8], [9, 10], [11, 12], [13, 14], [15, 16], [17, 18], [19, 1], [2, 3], [4, 5], [6, 7], [8, 9], [10, 11], [12, 13], [14, 15], [16, 17], [18, 1], [2, 19], [3, 5], [4, 6], [7, 9], [8, 10], [11, 13], [12, 14], [15, 17], [1, 16], [18, 2], [19, 3], [5, 7], [9, 4], [6, 8], [10, 13], [14, 11], [15, 12], [17, 1], [16, 2], [3, 18], [5, 19], [4, 7], [6, 9], [13, 8], [10, 14], [11, 15], [1, 12], [2, 17], [16, 3], [18, 5], [19, 4], [7, 13], [10, 6], [9, 14], [8, 11], [1, 15], [12, 2], [17, 3], [5, 16], [4, 18], [7, 19], [13, 6], [10, 1], [14, 8], [9, 11], [2, 15], [3, 12], [17, 5], [16, 4], [18, 7], [6, 19], [13, 1], [2, 10], [8, 15], [14, 3], [12, 9], [11, 5], [4, 17], [7, 16], [6, 18], [19, 13], [1, 8], [15, 10], [14, 2], [3, 9], [5, 12], [11, 4], [17, 7], [16, 6], [13, 18], [8, 19], [1, 14], [3, 10], [9, 15], [2, 5], [12, 4], [7, 11], [6, 17], [13, 16], [18, 8], [19, 14], [3, 1], [5, 10], [9, 2], [4, 15], [12, 7], [11, 6], [17, 13], [8, 16], [14, 18], [10, 19], [1, 5], [15, 3], [2, 4], [16, 19], [13, 9], [6, 12], [17, 11], [7, 14], [16, 18], [5, 8], [15, 19], [4, 10], [9, 1], [13, 3], [6, 2], [12, 17], [11, 16], [14, 5], [15, 7], [18, 19], [8, 4], [1, 6], [10, 12], [2, 13], [17, 9], [3, 11], [16, 14], [5, 15], [7, 1], [18, 10], [19, 12], [8, 2], [4, 13], [6, 3], [9, 16], [14, 17], [1, 11], [15, 18], [10, 7], [5, 13], [12, 8], [19, 9], [11, 2], [4, 14], [15, 6], [3, 7], [16, 10], [8, 17], [18, 12], [4, 1], [9, 5], [19, 11], [13, 15], [2, 7], [10, 17], [18, 9], [14, 6], [8, 3], [12, 16], [17, 19], [11, 18]],
  20: [[1, 2], [3, 4], [5, 6], [7, 8], [9, 10], [11, 12], [13, 14], [15, 16], [17, 18], [19, 20], [3, 1], [2, 4], [7, 5], [6, 8], [11, 9], [10, 12], [15, 13], [14, 16], [19, 17], [18, 20], [4, 1], [2, 3], [8, 5], [6, 7], [12, 9], [10, 11], [16, 13], [14, 15], [20, 17], [18, 19], [1, 5], [4, 8], [6, 2], [7, 3], [9, 13], [12, 16], [14, 10], [15, 11], [17, 1], [5, 20], [4, 18], [8, 19], [2, 7], [3, 6], [16, 9], [13, 12], [10, 15], [11, 14], [1, 20], [5, 17], [19, 4], [8, 18], [9, 2], [16, 7], [12, 3], [13, 6], [1, 10], [20, 15], [5, 11], [17, 14], [2, 19], [4, 9], [18, 7], [8, 16], [3, 13], [6, 12], [15, 1], [20, 10], [14, 5], [11, 17], [9, 19], [18, 2], [7, 4], [8, 3], [16, 6], [13, 1], [12, 15], [10, 5], [20, 14], [17, 9], [19, 11], [2, 8], [3, 18], [4, 6], [1, 7], [5, 16], [10, 13], [12, 14], [9, 15], [11, 20], [17, 2], [19, 3], [1, 8], [6, 18], [5, 4], [7, 10], [16, 11], [14, 9], [13, 20], [2, 12], [15, 17], [19, 1], [3, 5], [8, 10], [6, 11], [18, 16], [4, 14], [9, 7], [13, 2], [20, 12], [15, 19], [17, 3], [1, 6], [5, 18], [11, 8], [10, 16], [7, 14], [13, 4], [20, 9], [2, 15], [12, 19], [6, 17], [18, 1], [3, 11], [13, 5], [14, 8], [4, 10], [16, 20], [7, 15], [9, 6], [12, 17], [11, 2], [5, 19], [18, 13], [14, 1], [10, 3], [8, 20], [16, 4], [15, 6], [12, 7], [9, 5], [17, 13], [11, 18], [2, 14], [19, 10], [1, 16], [20, 3], [8, 15], [4, 12], [7, 13], [6, 14], [18, 9], [5, 2], [10, 17], [1, 11], [16, 19], [3, 15], [20, 4], [12, 8], [14, 18], [17, 7], [6, 10], [11, 13], [9, 1], [2, 16], [15, 5], [14, 19], [4, 17], [7, 20], [18, 12], [3, 9], [10, 2], [13, 8], [19, 6], [11, 4], [1, 12], [16, 17], [15, 18], [14, 3], [20, 2], [5, 12], [19, 7], [8, 9], [10, 18], [4, 15], [6, 20], [13, 19], [3, 16], [7, 11], [17, 8]]
}

const calculateResults = (pool, scores, maxScore, settings) => {
  const stats = []

  scores.forEach((row, rowIndex) => {
    stats.push({
      victories: 0,
      hitsScored: 0,
      hitsAgainst: 0,
      index: 0,
      id: pool.participants[rowIndex].uuid,
      points: 0
    })
  })

  scores.forEach((row, rowIndex) => {
    row.forEach((col, colIndex) => {
      if (rowIndex !== colIndex && !_.isNil(col) && !pool.participants[rowIndex].status && !pool.participants[colIndex].status) {
        if (_.first(col.toString()).toLowerCase() === 'v' && !_.isNil(scores[colIndex][rowIndex])) {
          stats[rowIndex].victories += 1
        }
        if (_.first(col.toString()).toLowerCase() === 'v') {
          if (col.toString().toLowerCase() === 'v') {
            stats[rowIndex].hitsScored += maxScore
            stats[colIndex].hitsAgainst += maxScore
          } else {
            const hitsFor = parseInt(col.toString().substring(1), 10)
            stats[rowIndex].hitsScored += hitsFor
            stats[colIndex].hitsAgainst += hitsFor
          }
        } else {
          const hitsFor = parseInt(col.toString(), 10)
          stats[rowIndex].hitsScored += hitsFor
          stats[colIndex].hitsAgainst += hitsFor
        }
      }
    })
  })

  _.each(stats, (stat) => {
    stat.points = Math.ceil((((stat.victories * settings.pointsPerWin) + (stat.hitsScored - stat.hitsAgainst)) / settings.maxPoolScore) * 100)
  })

  const activeParticipants = (pool.participants.filter(p => !p.status)).length
  const seeding = JSON.parse(JSON.stringify(stats))
  seeding.forEach((stat, index) => {
    stat.index = stat.hitsScored - stat.hitsAgainst
    stat.status = pool.participants[index].status
    stat.victoryPercent = stat.victories ? parseFloat(stat.victories / matchData[activeParticipants].length)?.toFixed(3) : '0.000'
  })

  seeding.sort((a, b) => {
    if (a.status === b.status) {
      if (a.victories === b.victories) {
        if (a.index === b.index) {
          return a.points > b.points ? -1 : 1
        } else {
          return a.index > b.index ? -1 : 1
        }
      } else {
        return a.victories > b.victories ? -1 : 1
      }
    } else {
      return _.get(a, 'status', 0) < b.status ? -1 : 1
    }
  })

  seeding.forEach((seed, index) => {
    if (index === 0) {
      seed.place = 1
    } else {
      if (seed.victories === seeding[index - 1].victories &&
        seed.index === seeding[index - 1].index &&
        seed.hitsScored === seeding[index - 1].hitsScored) {
        seed.place = seeding[index - 1].place
      } else {
        seed.place = seeding[index - 1].place + 1
      }
    }
  })

  let totalVictories = 0
  stats.forEach((stat, index) => {
    totalVictories += stat.victories
    stat.place = (seeding.find(s => s.id === stat.id))?.place
    stat.index = stat.hitsScored - stat.hitsAgainst
    pool.participants[index].stats = stat
    stat.status = pool.participants[index].status
    stat.victoryPercent = stat.victories ? parseFloat(stat.victories / (activeParticipants - 1))?.toFixed(3) : '0.000'
  })

  if (totalVictories === matchData[activeParticipants].length) {
    pool.complete = true
  }

  pool.scores = scores

  return pool
}

export {
  populatePools,
  generateAvailablePoolSizes,
  squadronHelper,
  matchData,
  calculateResults
}
